import { useLocation } from "react-router-dom";
import { Search, Heart, Upload } from "lucide-react";
import { useAdminMembers } from "./members/useAdminMembers";
import { MemberRow } from "./members/MemberRow";
import { Th } from "./members/parts";
import { ActivationLinkDialog } from "./members/ActivationLinkDialog";
import { AliancaImportDialog } from "./members/AliancaImportDialog";

export default function AdminMembers() {
  const location = useLocation();
  const initialAlianca = new URLSearchParams(location.search).get("alianca") || "all";
  const s = useAdminMembers(initialAlianca);

  return (
    <>
    <div className="px-8 py-10 lg:px-12 lg:py-14" data-testid="admin-members-page">
      <div className="overline">Sócios</div>
      <h1 className="display-heading text-4xl sm:text-5xl mt-3">Gestão de sócios.</h1>
      <span className="accent-rule mt-6 block" />

      <div className="mt-8 flex flex-wrap items-center gap-2 border-b border-[hsl(var(--border))]">
        {[
          { id: "pending", label: "Pendentes", count: s.counts.pending },
          { id: "approved", label: "Aprovados", count: s.counts.approved },
          { id: "rejected", label: "Rejeitados", count: s.counts.rejected },
          { id: "all", label: "Todos", count: s.counts.all },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => s.setTab(t.id)}
            className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${
              s.tab === t.id
                ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))] font-medium"
                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}
            data-testid={`members-tab-${t.id}`}
          >
            {t.label} <span className="ml-1 text-xs text-[hsl(var(--muted-foreground))]">({t.count})</span>
          </button>
        ))}
        <div className="relative ml-auto flex items-center gap-3">
          <div className="hidden md:inline-flex items-center gap-1 border border-[hsl(var(--border))] bg-white">
            {[
              { key: "all", label: "Todos" },
              { key: "with", label: "Com Aliança" },
              { key: "without", label: "Sem Aliança" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => s.setAliancaFilter(f.key)}
                className={`px-3 py-2 text-[11px] uppercase tracking-[0.16em] inline-flex items-center gap-1.5 ${
                  s.aliancaFilter === f.key
                    ? "bg-[hsl(var(--primary))] text-white"
                    : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                }`}
                data-testid={`alianca-filter-${f.key}`}
              >
                {f.key !== "all" && <Heart className="h-3 w-3" />}
                {f.label}
              </button>
            ))}
            <button
              onClick={() => s.setImportOpen(true)}
              className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] inline-flex items-center gap-1.5 border-l border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              title="Importar Aliança de Amor em massa"
              data-testid="alianca-import-open"
            >
              <Upload className="h-3 w-3" /> Importar
            </button>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <input
              value={s.q}
              onChange={(e) => s.setQ(e.target.value)}
              placeholder="Procurar…"
              className="pl-9 pr-3 py-2 bg-white border border-[hsl(var(--border))] text-sm w-56 focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="members-search"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-[hsl(var(--border))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
              <Th>Data</Th>
              <Th>Foto</Th>
              <Th>Nome</Th>
              <Th>Email</Th>
              <Th>Método</Th>
              <Th>Nível</Th>
              <Th>Função CIEES</Th>
              <Th>Comunidade</Th>
              <Th>Aliança</Th>
              <Th>Admissão</Th>
              <Th>Estado</Th>
              <th className="text-right text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {s.loading ? (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">A carregar…</td></tr>
            ) : s.filtered.length === 0 ? (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">Sem sócios neste estado.</td></tr>
            ) : (
              s.filtered.map((m) => (
                <MemberRow
                  key={m.id}
                  m={m}
                  cieesRoles={s.cieesRoles}
                  communities={s.communities}
                  handlers={{
                    act: s.act, getActivationLink: s.getActivationLink, remove: s.remove,
                    setTier: s.setTier, setJoinedAt: s.setJoinedAt, setCieesRole: s.setCieesRole,
                    setCommunity: s.setCommunity, resendApproval: s.resendApproval, load: s.load,
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
    <ActivationLinkDialog
      open={s.activationDialog.open}
      link={s.activationDialog.link}
      memberName={s.activationDialog.memberName}
      onClose={() => s.setActivationDialog({ open: false, link: "", memberName: "" })}
    />
    <AliancaImportDialog open={s.importOpen} onClose={() => s.setImportOpen(false)} onDone={s.load} />
    </>
  );
}
